/* Style */
import 'volto-form-block/components/Field.css';

import React from 'react';
import WysiwygWidget from '@plone/volto/components/manage/Widgets/WysiwygWidget';
import config from '@plone/volto/registry';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import CheckboxListWidget from 'volto-form-block/components/Widget/CheckboxListWidget';
import CheckboxWidget from 'volto-form-block/components/Widget/CheckboxWidget';
import DatetimeWidget from 'volto-form-block/components/Widget/DatetimeWidget';
import EmailWidget from 'volto-form-block/components/Widget/EmailWidget';
import FileWidget from 'volto-form-block/components/Widget/FileWidget';
import { HiddenWidget } from 'volto-form-block/components/Widget/HiddenWidget';
import NumberWidget from 'volto-form-block/components/Widget/NumberWidget';
import RadioWidget from 'volto-form-block/components/Widget/RadioWidget';
import SelectWidget from 'volto-form-block/components/Widget/SelectWidget';
import TextareaWidget from 'volto-form-block/components/Widget/TextareaWidget';
import TextWidget from 'volto-form-block/components/Widget/TextWidget';

import { DEFAULT_MAX_LENGTH } from '../../../components/Blocks/Form/maxLength';
import { DEFAULT_SIZE } from '../../../components/Blocks/Form/size';

const messages = defineMessages({
  select_a_value: {
    id: 'form_select_a_value',
    defaultMessage: 'Select a value',
  },
});

/**
 * Field class.
 * @class View
 * @extends Component
 *
 * Customização: adicionado suporte ao prop `placeholder` para campos.
 */
const Field = ({
  label,
  description,
  name,
  required,
  field_type,
  field_id,
  input_values,
  value,
  onChange,
  isOnEdit,
  valid,
  disabled = false,
  formHasErrors = false,
  errorMessage,
  id,
  // Customização: prop de placeholder para campos.
  placeholder,
  // Customização: comprimento máximo configurado no schema (max_length)
  max_length,
  // Customização: tamanho do campo numérico (size)
  size = DEFAULT_SIZE,
  // Customização: exibe a seleção de horário no campo de data (habilitado por padrão)
  show_time = true,
}) => {
  const intl = useIntl();

  const isInvalid = () => {
    return !isOnEdit && !valid;
  };

  const error = errorMessage ? [errorMessage] : [];

  const effectiveMaxLength =
    max_length !== undefined && max_length !== ''
      ? Number(max_length) === 0
        ? null
        : Number(max_length)
      : DEFAULT_MAX_LENGTH;

  return (
    <div className="field">
      {field_type === 'text' && (
        <TextWidget
          id={name}
          name={name}
          title={label}
          description={description}
          required={required}
          onChange={onChange}
          value={value}
          placeholder={placeholder}
          maxLength={effectiveMaxLength}
          error={error}
          isDisabled={disabled}
          invalid={isInvalid().toString()}
          {...(isInvalid() ? { className: 'is-invalid' } : {})}
        />
      )}
      {field_type === 'textarea' && (
        <TextareaWidget
          id={name}
          name={name}
          title={label}
          description={description}
          required={required}
          onChange={onChange}
          placeholder={placeholder}
          value={value}
          rows={10}
          error={error}
          isDisabled={disabled}
          invalid={isInvalid().toString()}
          {...(isInvalid() ? { className: 'is-invalid' } : {})}
        />
      )}
      {field_type === 'number' && (
        // Customização: passa o placeholder e size configurados para o widget numérico.
        <NumberWidget
          id={name}
          name={name}
          title={label}
          description={description}
          required={required}
          onChange={onChange}
          value={value}
          error={error}
          isDisabled={disabled}
          maxLength={effectiveMaxLength}
          invalid={isInvalid().toString()}
          placeholder={placeholder}
          size={size}
          {...(isInvalid() ? { className: 'is-invalid' } : {})}
        />
      )}
      {field_type === 'select' && (
        <SelectWidget
          id={name}
          name={name}
          title={label}
          description={description}
          getVocabulary={() => {}}
          getVocabularyTokenTitle={() => {}}
          choices={[...(input_values?.map((v) => [v, v]) ?? [])]}
          value={value}
          onChange={onChange}
          placeholder={intl.formatMessage(messages.select_a_value)}
          aria-label={intl.formatMessage(messages.select_a_value)}
          classNamePrefix="react-select"
          error={error}
          isDisabled={disabled}
          invalid={isInvalid().toString()}
          required={required}
          {...(isInvalid() ? { className: 'is-invalid' } : {})}
        />
      )}
      {field_type === 'single_choice' && (
        <RadioWidget
          id={name}
          title={label}
          description={description}
          required={required}
          onChange={onChange}
          valueList={[
            ...(input_values?.map((v) => ({ value: v, label: v })) ?? []),
          ]}
          value={value}
          error={error}
          isDisabled={disabled}
          invalid={isInvalid().toString()}
          {...(isInvalid() ? { className: 'is-invalid' } : {})}
        />
      )}
      {field_type === 'multiple_choice' && (
        <CheckboxListWidget
          id={name}
          name={name}
          title={label}
          description={description}
          required={required}
          onChange={onChange}
          valueList={[
            ...(input_values?.map((v) => ({ value: v, label: v })) ?? []),
          ]}
          value={value}
          error={error}
          isDisabled={disabled}
          invalid={isInvalid().toString()}
          {...(isInvalid() ? { className: 'is-invalid' } : {})}
        />
      )}
      {field_type === 'checkbox' && (
        <CheckboxWidget
          id={name}
          name={name}
          title={label}
          description={description}
          required={required}
          onChange={onChange}
          value={!!value}
          error={error}
          isDisabled={disabled}
          invalid={isInvalid().toString()}
          {...(isInvalid() ? { className: 'is-invalid' } : {})}
        />
      )}
      {field_type === 'date' && (
        <DatetimeWidget
          id={name}
          name={name}
          title={label}
          description={description}
          dateOnly={!show_time}
          noPastDates={false}
          resettable={false}
          onChange={onChange}
          value={value}
          error={error}
          isDisabled={disabled}
          required={required}
          invalid={isInvalid().toString()}
          {...(isInvalid() ? { className: 'is-invalid' } : {})}
        />
      )}
      {field_type === 'attachment' && (
        <FileWidget
          id={name}
          name={name}
          title={label}
          description={description}
          type="file"
          required={required}
          invalid={isInvalid().toString()}
          error={error}
          isDisabled={disabled}
          onChange={onChange}
          value={value}
          multiple={false}
        />
      )}
      {(field_type === 'from' || field_type === 'email') && (
        <EmailWidget
          id={name}
          name={name}
          title={label}
          description={description}
          required={required}
          onChange={onChange}
          value={value}
          error={error}
          isDisabled={disabled}
          invalid={isInvalid().toString()}
          {...(isInvalid() ? { className: 'is-invalid' } : {})}
        />
      )}
      {field_type === 'hidden' && (
        <HiddenWidget id={name} value={value} isOnEdit={isOnEdit} />
      )}
      {field_type === 'static_text' &&
        (isOnEdit ? (
          <WysiwygWidget
            wrapped={false}
            id={name}
            name={name}
            title={label}
            description={description}
            onChange={onChange}
            value={value}
          />
        ) : value?.data ? (
          <div
            className="static-text"
            dangerouslySetInnerHTML={{ __html: value.data }}
          />
        ) : (
          <br />
        ))}
      {config.blocks.blocksConfig.form.additionalFields?.reduce((acc, val) => {
        if (val.id === field_type)
          return [
            ...acc,
            <val.component
              id={name}
              name={name}
              key={name}
              title={label}
              description={description}
              required={required}
              onChange={onChange}
              value={value}
              error={error}
              isDisabled={disabled}
              formHasErrors={formHasErrors}
              invalid={isInvalid().toString()}
              {...(isInvalid() ? { className: 'is-invalid' } : {})}
            />,
          ];

        return acc;
      }, []) ?? []}
    </div>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
Field.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  description: PropTypes.string,
  required: PropTypes.bool,
  field_type: PropTypes.string,
  input_values: PropTypes.any,
  value: PropTypes.any,
  formHasErrors: PropTypes.bool,
  onChange: PropTypes.func,
  // Customização: placeholder para campo numérico.
  placeholder: PropTypes.string,
  // Customização: comprimento máximo configurado no schema (max_length).
  max_length: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Customização: tamanho do campo numérico (size).
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Customização: exibe a seleção de horário no campo de data.
  show_time: PropTypes.bool,
};

export default Field;
